import React, { useState, useEffect } from 'react';
import ChatHeader from '../molecules/ChatHeader';
import MessageList from '../organisms/MessageList';
import ChatFooter from '../molecules/ChatFooter';
import ApiKeyForm from '../molecules/ApiKeyForm';
import Typography from '../atoms/Typography';
import Button from '../atoms/Button';
import { useChat } from '../../context/ChatContext';
import './ChatTemplate.css';

const ChatTemplate = ({ onBackClick, interviewerName = "AI Interviewer" }) => {
  const [showInstructions, setShowInstructions] = useState(true);
  const [showPdfNotification, setShowPdfNotification] = useState(true);
  
  const { 
    messages, 
    userAvatar, 
    teacherAvatar, 
    isInitialized, 
    isLoading, 
    error, 
    isSpeaking,
    hasApiKey,
    hasPdfContent,
    interviewComplete,
    interviewScore,
    resetConversation,
    startConversation,
    mode,
    selectedChapter,
    conversationStarted,
    addCustomMessage
  } = useChat();

  // Add topics card as a message when conversation starts in tutor mode with a selected chapter
  useEffect(() => {
    if (mode === 'tutor' && conversationStarted && selectedChapter) {
      // Check if there's already a topics card in the messages
      const hasTopicsCard = messages.some(msg => msg.isTopicsCard);
      
      if (!hasTopicsCard) {
        // Add the topics card as a custom message
        addCustomMessage({
          content: selectedChapter,
          isUser: false,
          isTopicsCard: true
        });
      }
    }
  }, [mode, conversationStarted, selectedChapter, messages, addCustomMessage]);

  const handleRestartInterview = () => {
    resetConversation();
    // Wait a moment before restarting
    setTimeout(() => {
      startConversation();
    }, 500);
  };
  
  const handleCloseInstructions = () => {
    setShowInstructions(false);
  };
  
  const handleClosePdfNotification = () => {
    setShowPdfNotification(false);
  };

  return (
    <div className="chat-template">
      <ChatHeader 
        name={interviewerName} 
        status="online"
        avatarSrc={teacherAvatar}
        onBackClick={onBackClick}
      />
      
      <div className="chat-content">
        {!isInitialized && (
          <div className="chat-api-key-container glass-card">
            {!hasApiKey ? (
              <div className="env-instructions">
                <Typography variant="subtitle1" color="primary" className="heading-md text-gradient">
                  Missing OpenAI API Key
                </Typography>
                <Typography variant="body2" color="dark">
                  To use this application, you need to add your OpenAI API key to the environment.
                </Typography>
                <div className="code-block">
                  <code>
                    # Create a .env.local file in the project root and add:<br />
                    REACT_APP_OPENAI_API_KEY=your_openai_api_key
                  </code>
                </div>
                <Typography variant="body2" color="dark">
                  After adding the API key, restart the application.
                </Typography>
              </div>
            ) : (
              <ApiKeyForm />
            )}
          </div>
        )}
        
        <div className="chat-content-spacer"></div>
        
        {!hasPdfContent && (
          <div className="chat-pdf-required glass-card">
            <Typography variant="subtitle1" color="primary" className="heading-md text-gradient">
              Resume or Job Description Required
            </Typography>
            <Typography variant="body2" color="dark">
              To start the interview, you need to upload a resume or job description.
            </Typography>
            <Button 
              variant="primary"
              size="medium"
              onClick={onBackClick}
              className="upload-pdf-button"
            >
              Go to Upload Page
            </Button>
          </div>
        )}
        
        {error && (
          <div className="chat-error">
            <Typography variant="body2" color="primary">
              {error}
            </Typography>
          </div>
        )}
        
        {isLoading && (
          <div className="chat-loading">
            <Typography variant="body2" color="secondary">
              <span className="loading-dots">Loading</span>
            </Typography>
          </div>
        )}
        
        {isSpeaking && (
          <div className="chat-speaking-indicator">
            <Typography variant="caption" color="secondary">
              <span className="speaking-wave">Speaking</span>
            </Typography>
          </div>
        )}
        
        {interviewComplete && interviewScore && (
          <div className="interview-complete-container">
            <div className="interview-score-card">
              <Typography variant="h2" className="interview-score">
                {interviewScore}
              </Typography>
              <Typography variant="subtitle1" className="score-label">
                Interview Score
              </Typography>
              <Button 
                variant="primary" 
                size="medium"
                className="restart-button"
                onClick={handleRestartInterview}
              >
                Start New Interview
              </Button>
            </div>
          </div>
        )}
        
        {/* Only show the PDF notification for interview mode, not for tutor mode */}
        {hasPdfContent && !interviewComplete && showPdfNotification && mode === 'interview' && (
          <div className="chat-pdf-notification">
            <button className="close-button" onClick={handleClosePdfNotification}>×</button>
            <Typography variant="body2" color="success">
              <span className="pdf-indicator">Resume/Job Description Loaded</span>
              <span className="pdf-help">Answer the interview questions to practice your skills</span>
            </Typography>
          </div>
        )}
        
        {!interviewComplete && hasPdfContent && showInstructions && mode === 'interview' && (
          <div className="chat-instructions interview-card">
            <button className="close-button" onClick={handleCloseInstructions}>×</button>
            <div>
              <p><strong>Behavioral Interview Practice</strong></p>
              <p>Answer with the STAR method:</p>
              <p><strong>S</strong>ituation: Describe the context.</p>
              <p><strong>T</strong>ask: Explain your responsibility.</p>
              <p><strong>A</strong>ction: Detail what you did.</p>
              <p><strong>R</strong>esult: Share the outcome.</p>
            </div>
          </div>
        )}
        
        <MessageList 
          messages={messages}
          userAvatar={userAvatar}
          teacherAvatar={teacherAvatar}
          selectedChapter={selectedChapter}
        />
      </div>
      
      <ChatFooter interviewComplete={interviewComplete} />
      
      {interviewComplete && (
        <div className="interview-complete-footer">
          <Button 
            variant="primary" 
            size="large"
            onClick={handleRestartInterview}
            className="restart-interview-button"
          >
            Start New Interview
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChatTemplate; 